import { add } from 'date-fns/add';
import { addDays } from 'date-fns/addDays';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { startOfDay } from 'date-fns/startOfDay';
import { sub } from 'date-fns/sub';
import { subDays } from 'date-fns/subDays';

import type { AnkiDuration } from './types';

const oneDayInMinutes = 60 * 24;

export function minutesToAnkiDuration(minutes: number): AnkiDuration {
  if (minutes < oneDayInMinutes) {
    return {
      minutes: Math.floor(minutes),
    };
  }
  return {
    days: Math.floor(minutes / oneDayInMinutes),
  };
}

export function formatAnkiDuration(duration: AnkiDuration): string {
  if (duration.days && duration.days > 0) {
    return `${duration.days}d`;
  }
  if (duration.minutes && duration.minutes > 60) {
    return `${Math.round(duration.minutes / 60)}h`;
  }
  return `${duration.minutes}m`;
}

/**
 * `day`'s cutoff on the WALL CLOCK. `add(startOfDay(day), { hours: 4 })` adds four
 * ELAPSED hours, so on a day the clocks change it lands at 03:00 or 05:00 — and
 * `adjustedEndOfDay`, which steps with `addDays`, kept 04:00. On the night the
 * clocks went back, a card graded "1d" the day before fell due at 03:00, inside
 * the SR day it was graded in, and came straight back into the session.
 */
function cutoffOnDay(day: Date, cutoffTime: { hours: number; minutes?: number }): Date {
  const atCutoff = startOfDay(day);
  atCutoff.setHours(cutoffTime.hours, cutoffTime.minutes ?? 0, 0, 0);
  return atCutoff;
}

export function adjustedStartOfDay(
  baseDate: Date,
  cutoffTime: { hours: number; minutes?: number },
): Date {
  const todayAtCutoff = cutoffOnDay(baseDate, cutoffTime);
  if (isBefore(baseDate, todayAtCutoff)) {
    return subDays(todayAtCutoff, 1);
  }
  return todayAtCutoff;
}

export function adjustedEndOfDay(
  baseDate: Date,
  cutoffTime: { hours: number; minutes?: number },
): Date {
  const todayAtCutoff = cutoffOnDay(baseDate, cutoffTime);
  if (isBefore(baseDate, todayAtCutoff)) {
    return sub(todayAtCutoff, { seconds: 1 });
  }
  return sub(addDays(todayAtCutoff, 1), { seconds: 1 });
}

export function isAdjustedSameDay(
  date1: Date,
  date2: Date,
  cutoffTime: { hours: number; minutes?: number },
): boolean {
  const date1FirstSecond = adjustedStartOfDay(date1, cutoffTime);
  const date1LastSecond = adjustedEndOfDay(date1, cutoffTime);

  if (date1.getTime() === date2.getTime()) {
    return true;
  }

  return !isBefore(date2, date1FirstSecond) && !isAfter(date2, date1LastSecond);
}

/**
 * Add an Anki interval (days and/or minutes). Result is aligned to cutoff when crossing a day boundary.
 */
export function adjustedDayAdd(
  baseDate: Date,
  ankiDuration: AnkiDuration,
  cutoffTime: { hours: number; minutes?: number },
): Date {
  const nextMoment = add(baseDate, ankiDuration);
  const todayAtCutoff = cutoffOnDay(baseDate, cutoffTime);

  const isBeforeCutoff = isBefore(baseDate, todayAtCutoff);
  const daysToAddForTomorrowCutoff = isBeforeCutoff ? 0 : 1;

  const tomorrowStart = cutoffOnDay(
    addDays(baseDate, daysToAddForTomorrowCutoff),
    cutoffTime,
  );

  if (isBefore(nextMoment, tomorrowStart)) {
    return nextMoment;
  }
  return cutoffOnDay(nextMoment, cutoffTime);
}
